import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Chapter } from "@/components/Chapter";
import { ArrowUpRight, Check, Mail, MapPin, Menu, X } from "lucide-react";

const NAV = [
  { label: "Coverage", href: "#coverage" },
  { label: "Staffing", href: "#staffing" },
  { label: "Audits", href: "#audits" },
  { label: "Technology", href: "#technology" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  {
    id: "coverage",
    num: "01",
    title: "Site Safety Coverage",
    desc: "Experienced field EHS on your project by the week.",
    bullets: [
      "Daily walks, audits and permit checks",
      "JHAs, toolbox talks, orientations",
      "NFPA 70E and energized-work oversight",
    ],
  },
  {
    id: "staffing",
    num: "02",
    title: "Safety Staffing",
    desc: "Vetted local safety technicians placed on your job — managed by us, on one invoice.",
    bullets: [
      "OSHA 30 + First Aid/CPR on every tech",
      "No per diem",
    ],
  },
  {
    id: "audits",
    num: "03",
    title: "Audits, Programs & Training",
    desc: "Deliverables, not open-ended hours.",
    bullets: [
      "Site Safety Audits with photo-documented findings",
      "Prequalification support and written programs",
      "NFPA 70E awareness training",
    ],
  },
  {
    id: "technology",
    num: "04",
    title: "WSafe Technology",
    desc: "AI hazard detection on the cameras you already have, plus a phone-based inspection app.",
    bullets: [
      { text: "WSafe CCTV", href: "https://wsafe.si" },
      { text: "WSafe.ai mobile inspections", href: "https://wsafe.ai" },
    ],
  },
];

export default function FieldServices() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "WSafe Field Services — A division of WorkSafeAgent LLC";
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-ink">
      <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-xl border-b border-line">
        <div className="mx-auto max-w-[1160px] px-6 h-16 flex items-center justify-between">
          <a href="#top" data-testid="fs-nav-logo" className="flex items-center gap-2.5">
            <span className="w-3 h-3 bg-safety" />
            <span className="text-base font-extrabold text-navy tracking-tight">WSafe Field Services</span>
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`fs-nav-${l.label.toLowerCase()}`}
                className="text-sm font-semibold text-ink hover:text-safety transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              data-testid="fs-nav-cta"
              href="/contact?topic=pilot"
              className="hidden sm:inline-block rounded-lg bg-safety text-white text-sm font-bold px-4 py-2 hover:bg-[#d97c12] transition-colors"
            >
              Book a pilot
            </a>
            <button
              data-testid="fs-nav-menu-toggle"
              className="lg:hidden text-ink"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="lg:hidden border-t border-line bg-white px-6 py-4 flex flex-col gap-4">
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm font-semibold text-ink"
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}
      </header>

      <section id="top" className="relative">
        <div className="bg-gradient-to-b from-navy to-navydeep">
          <div className="mx-auto max-w-[1160px] px-6 pt-36 pb-24 md:pt-44 md:pb-32">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70 mb-6">
                Abilene · West Texas
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="text-[36px] sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white max-w-4xl">
                Mission-critical safety, <span className="text-safety">delivered local.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
                Site safety coverage, vetted safety staffing, audits and training, and AI hazard
                detection for data center, solar, BESS and industrial construction.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  data-testid="fs-hero-cta-walk"
                  href="/contact?topic=pilot"
                  className="rounded-lg bg-safety text-white font-bold px-7 py-3.5 hover:bg-[#d97c12] transition-colors"
                >
                  Book a pilot
                </a>
                <a
                  data-testid="fs-hero-cta-services"
                  href="#coverage"
                  className="rounded-lg border-2 border-white/40 text-white font-bold px-7 py-3.5 hover:border-white hover:bg-white/5 transition-colors"
                >
                  See the services
                </a>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="hazard-yellow" aria-hidden="true" />
      </section>

      <section className="bg-white py-[88px]">
        <div className="mx-auto max-w-[1160px] px-6">
          <Chapter
            eyebrow="What we do"
            title="Four ways we cover your site."
            lede="Field-delivered safety for data center, solar, BESS and industrial construction — by people based in Abilene, not flown in."
          />
          <div className="grid md:grid-cols-2 gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 2) * 0.1} className="h-full">
                <div
                  id={s.id}
                  data-testid={`fs-card-${s.id}`}
                  className="scroll-mt-24 h-full bg-white border border-line border-t-[3px] border-t-safety rounded-[14px] p-8 shadow-[0_2px_10px_rgba(23,71,95,0.05)]"
                >
                  <p className="text-xs font-bold text-safety mb-6">{s.num}</p>
                  <h3 className="text-2xl font-extrabold text-navy mb-2">{s.title}</h3>
                  <p className="text-sm text-steel mb-6">{s.desc}</p>
                  <ul className="space-y-3">
                    {s.bullets.map((b) => (
                      <li key={typeof b === "string" ? b : b.text} className="flex items-start gap-3 text-sm text-ink">
                        <Check size={16} className="text-safety shrink-0 mt-0.5" />
                        {typeof b === "string" ? (
                          b
                        ) : (
                          <a
                            href={b.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`fs-tech-link-${b.href.replace(/\W+/g, "")}`}
                            className="inline-flex items-center gap-1.5 font-bold text-safety hover:text-[#d97c12] transition-colors"
                          >
                            {b.text} <ArrowUpRight size={13} />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="bg-wash py-[88px] scroll-mt-24">
        <div className="mx-auto max-w-[1160px] px-6">
          <Chapter
            eyebrow="Pricing"
            title="One price list, in writing."
            lede="Field Services uses the same universal WorkSafeAgent pricing as the software — quoted before work starts."
          />
          <Reveal>
            <a
              href="/pricing"
              data-testid="fs-pricing-link"
              className="inline-flex items-center gap-2 rounded-lg bg-safety text-white font-bold px-7 py-3.5 hover:bg-[#d97c12] transition-colors mb-10"
            >
              See universal pricing <ArrowUpRight size={16} />
            </a>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            <Reveal delay={0.1} className="h-full">
              <div data-testid="fs-pricing-compliance" className="h-full bg-white border border-line rounded-[14px] p-8 shadow-[0_2px_10px_rgba(23,71,95,0.05)]">
                <h3 className="text-xl font-extrabold text-navy mb-4">Compliance Evaluation &amp; Documentation Service</h3>
                <p className="text-sm text-steel leading-relaxed">
                  $1,500 evaluation; then Essentials $500, Standard $1,000 or Premium $2,000 per site/month,
                  billed in addition to a Core or Pro subscription. Covers OSHA and environmental records.
                  Documentation setup is included in the setup fee; work beyond included hours is $150/hr.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="h-full">
              <div data-testid="fs-pricing-consulting" className="h-full bg-white border border-line rounded-[14px] p-8 shadow-[0_2px_10px_rgba(23,71,95,0.05)]">
                <h3 className="text-xl font-extrabold text-navy mb-4">Consulting</h3>
                <p className="text-sm text-steel leading-relaxed">
                  $150/hr; $1,500 gap review; $1,500/day audit; $2,500 AI readiness assessment (credited
                  toward a paid pilot); $1,200/month retainer. No travel charge for on-site work within 50
                  miles of Abilene, Texas.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="contact" className="bg-white py-[88px] scroll-mt-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-safety mb-4">Contact</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy leading-tight">
              Book a pilot.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-steel leading-relaxed max-w-xl mx-auto">
              Tell us what you're building and what the GC or owner is asking for. You'll have a written
              scope.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-col items-center gap-6">
              <a
                data-testid="fs-contact-pilot"
                href="/contact?topic=pilot"
                className="rounded-lg bg-safety text-white font-bold px-10 py-4 hover:bg-[#d97c12] transition-colors"
              >
                Book a pilot
              </a>
              <a
                data-testid="fs-contact-email"
                href="mailto:info@wsafeai.com"
                className="inline-flex items-center gap-3 text-sm md:text-base font-bold text-navy hover:text-safety transition-colors"
              >
                <Mail size={16} /> info@wsafeai.com
              </a>
              <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-steel">
                <MapPin size={14} /> Based in Abilene, Texas, serving West Texas.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="bg-navydeep">
        <div className="mx-auto max-w-[1160px] px-6 py-14 flex flex-col items-center gap-4 text-center">
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 bg-safety" />
            <span className="text-base font-extrabold text-white tracking-tight">WSafe Field Services</span>
          </div>
          <p data-testid="fs-footer-division" className="text-xs text-white/60">
            WSafe Field Services is a division of WorkSafeAgent LLC.
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">
            <a href="https://wsafe.ai" target="_blank" rel="noopener noreferrer" data-testid="fs-footer-wsafe-ai" className="hover:text-safety transition-colors">
              wsafe.ai
            </a>
            <a href="https://wsafe.si" target="_blank" rel="noopener noreferrer" data-testid="fs-footer-wsafe-si" className="hover:text-safety transition-colors">
              wsafe.si
            </a>
            <a href="/" data-testid="fs-footer-hub" className="hover:text-safety transition-colors">
              worksafeagent.com
            </a>
          </div>
          <p className="text-[11px] text-white/30">© 2026 WorkSafeAgent LLC</p>
        </div>
      </footer>
    </div>
  );
}
